import moment, { Moment } from 'moment'
import { getFilterKeys } from 'src/components/NewFilterComponent/utils/filterUtils'
import { get } from 'src/utils/config/lodashUtils'
import { FilterNode } from './FilterNode'
import { ApiFilterResponseType, FilterGraphFilterValueType } from './types'

export type DateRangeType = { start: Moment; end: Moment } | undefined

const filterKeys: string[] = getFilterKeys()

export class FilterGraph {
  private filters: {
    [key: string]: FilterGraphFilterValueType
  }
  private dateRange: DateRangeType
  private lastDate: Moment
  private graphId: string

  constructor(graphId: string, filtersData: ApiFilterResponseType[], singleSelectFilterKeys?: string[]) {
    this.graphId = graphId
    this.filters = {}
    filterKeys.forEach((key) => {
      this.filters[key] = {
        allPossible: [],
        visible: new Set<FilterNode>(),
        selected: new Set<FilterNode>(),
        isSingleSelect: singleSelectFilterKeys && singleSelectFilterKeys.includes(key)
      }
    })
    this.dateRange = {
      start: moment().subtract(30, 'days'),
      end: moment()
    }
    this.lastDate = moment()

    this.generateGraph(filtersData)
    this.reset()
  }

  public getFilters() {
    return this.filters ? this.filters : null
  }

  public getAllPossibleNodesByKey(key: string) {
    if (key in this.filters) {
      return this.filters[key].allPossible
    }
    return []
  }

  public getVisibleNodesByKey(key: string) {
    if (key in this.filters) {
      return this.filters[key].visible
    }
    return new Set<FilterNode>()
  }

  public setVisible(key: string, newVisible: Iterable<FilterNode>) {
    if (!(key in this.filters)) {
      return
    }
    this.filters[key].visible = new Set(newVisible)
  }

  public getSelectedNodesByKey(key: string) {
    if (key in this.filters) {
      return this.filters[key].selected
    }
    return new Set<FilterNode>()
  }

  public setSelected(key: string, newSelected: Iterable<FilterNode>) {
    if (!(key in this.filters)) {
      return
    }
    this.filters[key].selected = new Set(newSelected)
  }

  public getSingleSelectByKey(key: string) {
    if (key in this.filters) {
      return this.filters[key].isSingleSelect
    }
    return false
  }

  public getDateRange() {
    return this.dateRange
  }

  public setDateRange(newDateRange: DateRangeType) {
    this.dateRange = { ...newDateRange }
  }

  public getLastDate() {
    return this.lastDate
  }

  public setLastDate(newLastDate: Moment) {
    this.lastDate = newLastDate
  }

  /**
   * If all filters are multiple select, this resets their visible to all possible, and selected to all possible
   * If there are single select filters, this optimally selects first available options and updates visibility
   */
  public reset() {
    const allKeys = Object.keys(this.filters)
    const singleSelectKeys = allKeys.filter(key => this.filters[key].isSingleSelect)
    const multiSelectKeys = allKeys.filter(key => !this.filters[key].isSingleSelect)
    
    // Step 1: Reset all filters to initial state
    allKeys.forEach((key) => {
      this.filters[key].visible = new Set(this.filters[key].allPossible)
      
      if (this.filters[key].isSingleSelect) {
        this.filters[key].selected = this.filters[key].allPossible.length > 0 
          ? new Set([this.filters[key].allPossible[0]]) 
          : new Set()
      } else {
        this.filters[key].selected = new Set(this.filters[key].allPossible)
      }
    })
    
    // Step 2: If no single select filters, we're done
    if (singleSelectKeys.length === 0) return
    
    // Step 3: Update multi-select visibility using optimized intersection computation
    multiSelectKeys.forEach(observerKey => {
      const intersection = this.computeFilterIntersection(observerKey, singleSelectKeys)
      if (intersection.size > 0) {
        this.filters[observerKey].visible = intersection
      }
    })
  }

  /**
   * Efficiently computes the intersection of related nodes for a multi-select filter
   * based on the selections from all single-select filters
   */
  private computeFilterIntersection(observerKey: string, singleSelectKeys: string[]): Set<FilterNode> {
    if (singleSelectKeys.length === 0) {
      return new Set(this.filters[observerKey].allPossible)
    }
    
    let intersection: Set<FilterNode> | null = null
    
    // For each single select filter, get its relations to the observer
    for (const subjectKey of singleSelectKeys) {
      if (observerKey === subjectKey) continue
      
      const selectedNodes = Array.from(this.filters[subjectKey].selected)
      if (selectedNodes.length === 0) continue
      
      const selectedNode = selectedNodes[0] // Single select has only one selection
      const relations = selectedNode.relations[observerKey]
      
      if (!relations || relations.size === 0) continue
      
      // Get all nodes related to this single select filter's selection
      const currentRelatedNodes = new Set<FilterNode>()
      for (const [, relationData] of relations) {
        currentRelatedNodes.add(relationData.node)
      }
      
      // Compute intersection with previous results
      if (intersection === null) {
        intersection = currentRelatedNodes
      } else {
        // Efficient Set intersection using native Set methods
        intersection = new Set([...intersection].filter(node => currentRelatedNodes.has(node)))
      }
      
      // Early exit if intersection becomes empty
      if (intersection.size === 0) break
    }
    
    return intersection || new Set(this.filters[observerKey].allPossible)
  }

  public generateGraph(filtersData: ApiFilterResponseType[]) {
    const nodesMap: {
      [key: string]: Map<string | number, FilterNode>
    } = {}
    filterKeys.forEach((key) => {
      nodesMap[key] = new Map<string | number, FilterNode>()
    })

    filtersData.forEach((filtersDataObject) => {
      // * create nodes
      filterKeys.forEach((key) => {
        if (!(key in filtersDataObject)) {
          return
        }
        if (!nodesMap[key].has(filtersDataObject[key])) {
          const node = new FilterNode(filtersDataObject[key], key)
          nodesMap[key].set(filtersDataObject[key], node)
          this.filters[key].allPossible.push(node)
        }
      })

      // * add relations
      filterKeys.forEach((currentKey) => {
        if (!(currentKey in filtersDataObject)) {
          return
        }
        const currentNode = nodesMap[currentKey].get(filtersDataObject[currentKey])

        filterKeys.forEach((relatedKey) => {
          // * a node is not related to itself
          if (currentKey === relatedKey) return

          const relatedNode = nodesMap[relatedKey].get(filtersDataObject[relatedKey])
          // * bidirectional relation
          if (currentNode.relations[relatedKey].has(relatedNode.value) && relatedNode.relations[currentKey].has(currentNode.value)) {
            currentNode.relations[relatedKey].get(relatedNode.value).slugs.add(filtersDataObject.slug)
            relatedNode.relations[currentKey].get(currentNode.value).slugs.add(filtersDataObject.slug)
          } else {
            currentNode.relations[relatedKey].set(relatedNode.value, { node: relatedNode, slugs: new Set([filtersDataObject.slug]) })
            relatedNode.relations[currentKey].set(currentNode.value, { node: currentNode, slugs: new Set([filtersDataObject.slug]) })
          }
        })
      })
    })
  }

  /**
   * this function will take the intersection from visible and selected Set
   * and returns the resultant Set (used in getFilters function, FilterComponent UI)
   * @param key
   * @returns
   */
  public getVisibleSelectedIntersectionNodes(key: string) {
    const result = new Set<FilterNode>()
    if (key in this.filters) {
      const obj = this.filters[key]
      const visibleSet = get(obj, 'visible', new Set<FilterNode>()) as Set<FilterNode>
      const selectedSet = get(obj, 'selected', new Set<FilterNode>()) as Set<FilterNode>

      visibleSet.forEach((value: FilterNode) => {
        if (selectedSet.has(value)) {
          result.add(value)
        }
      })
    }
    return result
  }
}
